import * as z from "zod";

export const SEARCH_CONNECTORS_PAGE_SIZE = 50;

export const searchConnectorsInput = z.object({
  access: z
    .enum(["usable", "readable"], {
      error:
        "Set access to usable to find connectors eligible for use, or readable to inspect metadata only.",
    })
    .optional()
    .describe(
      "Defaults to usable: find connectors eligible for use here. Choose readable to include connectors the caller can read even when unavailable here. Both modes return the same metadata fields."
    ),
  search: z
    .string()
    .optional()
    .describe("Search connector names or identifiers."),
  targetUrl: z.string().optional().describe("Match connectors for a full URL."),
  limit: z
    .number()
    .int()
    .positive()
    .nullable()
    .optional()
    .describe(
      "Maximum results per page. Defaults to 50; set null for all matches."
    ),
  offset: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe("Pass nextOffset to fetch the next page. Defaults to 0."),
  createdAfter: z
    .string()
    .datetime({ offset: true })
    .optional()
    .describe("Keep connectors created after this time."),
  sort: z
    .enum(["name", "created_at"])
    .optional()
    .describe("Sort by name (default) or creation time."),
  order: z.enum(["asc", "desc"]).optional().describe("Defaults to ascending."),
});

export const searchConnectorsRequestSchema = searchConnectorsInput
  .omit({ search: true, targetUrl: true, createdAfter: true })
  .extend({
    offset: searchConnectorsInput.shape.offset.describe(
      "Pass next_offset to fetch the next page. Defaults to 0."
    ),
    q: searchConnectorsInput.shape.search,
    target_url: searchConnectorsInput.shape.targetUrl,
    created_after: searchConnectorsInput.shape.createdAfter,
    workflow_id: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Required for usable searches. Optional for readable searches to report workflow attachment status."
      ),
  });

const connectorsSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    identifier: z.string(),
    product: z.string(),
    url_pattern: z.string(),
    draft: z.boolean(),
    note: z.string().nullable(),
    ai_context: z.string().nullable(),
    last_test_status: z.enum(["pending", "success", "failure"]).nullable(),
    last_test_at: z.string().datetime().nullable(),
    last_test_message: z.string().nullable(),
    authorization_mode: z.enum(["owner", "visitor"]),
    created_at: z.string().datetime(),
    created_by_type: z.enum(["user", "service_account"]).nullable(),
    created_by_id: z.string().nullable(),
    in_use: z.boolean().nullable(),
  })
);

export const searchConnectorsResponseSchema = z.object({
  connectors: connectorsSchema,
  total_matched: z.number().int().nonnegative(),
  next_offset: z.number().int().nonnegative().nullable(),
  url_fallback: z.boolean(),
  suggestions: z.array(
    z.object({
      identifier: z.string(),
      name: z.string(),
      product: z.string(),
      tag_line: z.string().nullable(),
      ai_context: z.string().nullable(),
      create_url: z.string(),
    })
  ),
});

export type SearchConnectorsResponse = z.infer<
  typeof searchConnectorsResponseSchema
>;

export type SearchConnectorsInput = z.infer<typeof searchConnectorsInput>;
export type SearchConnectorsRequest = z.infer<
  typeof searchConnectorsRequestSchema
>;

export const searchConnectorsDescription =
  "Search connectors by name or target URL. access defaults to usable: return connectors the caller may use in the target scope, including ones not yet attached. Choose access: readable to find connectors under read permission without requiring a workflow; these results do not establish use eligibility. Both modes return the same fields: connector identity, URL pattern, note, API guidance, saved connection-test status, authorization mode (owner uses the owner’s account; visitor uses each caller’s account), creator, creation time, and attachment status. Attachment status is null without a workflow or chat scope; workflow attachment status covers all branches and chat approvals. Results are returned in connectors. Both responses include the total matched, next-page offset, URL-fallback flag, and suggestions with API guidance and browser creation links. Both modes support creation-date filtering, sorting, limit, and offset, with 50 results per page by default; set limit: null for all matches. In usable searches, a URL with no pattern match can fall back to a name match, explicitly marked in the result, so verify the intended service before use. Usable suggestions exclude connector types already matched here; readable suggestions list matching supported types. Neither means nobody has configured a connector. Search with access: usable before selecting one for use. Connectors sharing a product are variants of the same service, each with its own credential. Neither mode attaches connectors or authorizes execution.";
